import { useState, useMemo, useEffect } from "react";
import { useQueryClient } from 'react-query';
import URL from 'core/resources/urls';
import moment from "moment";
import { showError } from "infra/helpers/alerta";
import { useFilters } from "core/features/filters/store";
import { useFiltersLists } from "core/features/filters/store/lists";
import useMapData from "../../hooks/useMapData";
import useStore from "../OrdemServico/hooks/useStore";
import { useCreateOrdemServico, useUpdateOrdemServico } from "../OrdemServico/hooks/useCreateOrdemServico";
import useGetMatrizOperacoes from "../OrdemServico/hooks/useGetMatrizOperacoes";
import { useGetOrdemServico } from "../OrdemServico/hooks/useGetOrdemServico";
import { useGetApontamentoTalhoes } from "../OrdemServico/hooks/useGetApontamentoTalhoes";
import ModalMatrizOperacoes from "./components/ModalMatrizOperacoes";
import ModalCadastroOrdemServico from "./components/ModalCadastroOrdemServico";
import ModalLocaisRecursos from "./components/ModalLocaisRecursos";
import ModalResultado from "./components/ModalResultado";
import { isBlocked, isNewGroup } from "./components/ModalMatrizOperacoes/utils";
import { todasClassesSelecionadas } from "../ModalOrdemServicoMatriz/utils";
import useFiltro from "../../hooks/useFiltro";

interface OrdemServicoProps {
  planoOperacionalData: any;
  onClose: () => void;
  isEdit?: boolean;
  ordemServico: {
    id: string | null;
    code: number | null;
    preCaldaCarregada: boolean;
    preCaldaProgramada: boolean;
  };
  listMap: any[];
}

function OrdemServico({ planoOperacionalData, onClose, isEdit = false, ordemServico, listMap }: OrdemServicoProps) {

  const { filters } = useFilters();
  const queryClient = useQueryClient();
  const { filtroExecucaoOPeracional } = useFiltro();
  const { filtersAdvanced, unidade, safra } = filters;
  const { classeOperacao, operacao: operacaoFiltro, safraPeriodo } = filtersAdvanced;

  const { listTalhoesIds, setListTalhoesIds } = useMapData();
  const talhoesIds = useMemo(() => Array.from(listTalhoesIds), [listTalhoesIds]);
  const { data, isFetching, isLoading } = useGetMatrizOperacoes(talhoesIds, unidade?.id, safraPeriodo, operacaoFiltro, classeOperacao);
  const { data: ordemServiceData } = useGetOrdemServico(ordemServico?.id || "");
  const { data: apontamentoTalhoes } = useGetApontamentoTalhoes(ordemServico?.id || "", talhoesIds);
  const { unidades, filtrosAvancadosOS } = useFiltersLists();
  const unidadeIdExterno = unidades?.find((u: any) => u?.id === unidade?.id)?.idExterno;
  const safraPeriodoIdExterno = filtrosAvancadosOS?.safraPeriodos?.find((s: any) => s?.safraPeriodoId === filtersAdvanced?.safraPeriodo)?.safraPeriodoIdExterno;

  const listData = useMemo(() => {
    const selectedIds = new Set(talhoesIds.map(String));

    return listMap
      ?.filter((list: any) => selectedIds.has(String(list?.properties?.id)))
      ?.map((list: any) => ({
        codigo: list?.properties?.codigo ?? list?.id,
        areaPlantadaHa: list?.properties?.tamanhoAreaHa,
        talhaoIdExterno: list?.properties?.talhaoIdExterno,
      }));
  }, [listMap, talhoesIds]);

  const {
    centroCusto,
    equipe,
    operacao,
    previsaoInicio,
    previsaoEncerramento,
    sistemaAplicacao,
    tipoBico,
    vazaoProgramada,
    quantidadeTanques,
    capacidadeTanque,
    volumeTanqueExtra,
    larguraFaixa,
    observacoes,
    setClassesOperacoes,
    setPlanoOperacionalOperacaoTalhaoIds,
    setIsMatrizSubmitted,
    isMatrizSubmitted,
    planoOperacionalOperacaoTalhaoIds,
  } = useStore();

  const { mutateAsync: createOrderService, isLoading: loadingCreate } = useCreateOrdemServico();
  const { mutateAsync: updateOrderService, isLoading: loadingUpdate } = useUpdateOrdemServico();
  const idUnidade = unidade?.id;
  const safraId = safra?.id;

  const [qtdTalhoes, setQtdTalhoes] = useState(0);
  const [isMatrizOperacoesOpen, setIsMatrizOperacoesOpen] = useState(true);
  const [isCadastroOrdemServicoOpen, setIsCadastroOrdemServicoOpen] = useState(false);
  const [isLocaisRecursosOpen, setIsLocaisRecursosOpen] = useState(false);
  const [isResultadoOpen, setIsResultadoOpen] = useState(false);
  const [numeroOs, setNumeroOs] = useState<{ numero: number | null; ordemServicoId: string | null }>();
  const [operationName, setOperationName] = useState("");
  const [result, setResult] = useState(data || []);
  const [statuses, setStatuses] = useState(false);

  async function handleCreateOrUpdate(locais: any[], recursos: any[]) {
    try {
      const ordemServicoLocais = locais.map(local => {
        let res: any = {
          area: Number(local?.areaEfetiva)?.toFixed(2),
          talhaoIdExterno: local?.talhaoIdExterno,
          planoOperacionalOperacaoTalhaoIds: local?.planoOperacionalOperacaoTalhao?.map((p: any) => p.planoOperacionalOperacaoTalhaoId),
          ordemServicoTalhaoIdExterno: ordemServiceData?.talhoes?.find((t: any) => t.talhaoIdExterno === local?.talhaoIdExterno)?.idExterno ?? undefined
        }
        return res;
      });

      const ordemServicoRecursos = recursos?.map(recurso => {
        let res: any = {
          dose: Number(recurso?.quantidade),
          insumoIdExterno: recurso?.insumoIdExterno,
          utilizacao: Number(recurso?.porcentagemUtilizacao),
          alvoIdsExternos: recurso?.alvoIdsExternos,
          componenteIdExterno: recurso?.componenteIdExterno,
          quantidadeProducao: Number(recurso?.quantidadeProducao),
          ordemServicoComponenteIdExterno: ordemServiceData?.componentes?.find((c: any) => c.componenteIdExterno === recurso?.componenteIdExterno)?.idExterno ?? undefined
        }

        return res;
      });

      let body: any = {
        dataAbertura: moment().toISOString(),
        dataPrevisaoInicio: moment(previsaoInicio).toISOString(),
        dataPrevisaoEncerramento: moment(previsaoEncerramento).toISOString(),
        equipeIdExterno: equipe,
        centroCustoIdExterno: centroCusto,
        operacaoIdExterno: operacao,
        observacao: observacoes,
        unidadeIdExterno,
        quantidadeVazaoProgramada: Number(vazaoProgramada),
        quantidadeCapacidadeTanque: Number(capacidadeTanque),
        quantidadeTanque: quantidadeTanques,
        volumeTanqueExtra,
        tipoBicoPulverizadorIdExterno: tipoBico,
        sistemaAplicacaoIdExterno: sistemaAplicacao,
        larguraFaixa,
        safraPeriodoIdExterno,
        ordemServicoLocais,
        ordemServicoRecursos,
      }

      let result = null;
      if (isEdit && ordemServico?.id) {
        body.ordemServicoId = ordemServico.id;
        delete body.dataAbertura;
        result = await updateOrderService({ id: ordemServico.id, body });
        setNumeroOs({
          ordemServicoId: ordemServico.id,
          numero: ordemServico.code
        });
      } else {
        result = await createOrderService(body);
        setNumeroOs(result?.data);
      }

      const newFilters = {
        idUnidade: unidade?.id,
        idSafra: safra?.id,
        menuName: "Gestão de O.S."
      }

      queryClient.invalidateQueries([URL.PLANO_OPERACIONAL.OBTER_MAPA, idUnidade, filtroExecucaoOPeracional]);
      queryClient.invalidateQueries([URL.ORDEM_SERVICO.OBTER_LISTAS, newFilters]);
      queryClient.invalidateQueries([URL.PLANO_OPERACIONAL.OBTER_MAPA_ORDEM_SERVICO, idUnidade, safraId, filtroExecucaoOPeracional]);
      setQtdTalhoes(locais.length);
      handleOpenResultado();
    } catch (error: any) {
      if (error.response) {
        const message = error.response?.data?.Details?.[0]?.Errors?.[0];
        showError(message ?? "Erro ao criar a O.S.");
        return;
      }
      showError("Erro ao criar a O.S.");
    }
  }

  useEffect(() => {
    if (isEdit && ordemServico?.code && data && data?.length > 0) {
      const dataSelected = data
        .flatMap((operacao: any) => operacao.planoOperacionalOperacaoTalhao)
        .filter((plano: any) => plano.ordensServico?.includes(ordemServico.code));

      if (dataSelected?.length > 0 && !isFetching && planoOperacionalOperacaoTalhaoIds.length === 0) {
        setPlanoOperacionalOperacaoTalhaoIds(dataSelected.map((item: any) => item.planoOperacionalOperacaoTalhaoId));
        setClassesOperacoes(dataSelected.map((item: any) => item.operacaoClasse));

        setStatuses(dataSelected?.some((d: any) => d.status?.includes("Executada")))
      }
    }
  }, [data, ordemServico?.code, isEdit]);

  useEffect(() => {
    if (data) {
      const newData = data.map((operacao: any) => {
        const listaFiltrada = operacao?.planoOperacionalOperacaoTalhao
          ?.filter((item: any) => !operationName || item.operacaoClasse === operationName)
          ?.sort((a: any, b: any) => {
            const classeCompare = a.operacaoClasse.localeCompare(b.operacaoClasse);
            if (classeCompare !== 0) return classeCompare;
            return a.dataProgramada.localeCompare(b.dataProgramada);
          })
          ?.map((talhao: any, index: number, arr: any[]) => {
            const talhaoAnterior = arr[index - 1];
            const newGroup = isNewGroup(talhao.operacaoClasse, index, arr);

            return {
              ...talhao,
              numeroTalhao: operacao.codigo,
              bloqueado: isBlocked(talhao, index, newGroup, talhaoAnterior, isEdit),
              disabled: isBlocked(talhao, index, newGroup, talhaoAnterior),
              disabledCopy: isBlocked(talhao, index, newGroup, talhaoAnterior),
              error: false
            }
          });

        return {
          ...operacao,
          planoOperacionalOperacaoTalhao: listaFiltrada,
        }
      });
      setResult(newData)
    }
  }, [data, operationName]);

  function contagemClasses(talhoes: any[]) {
    if (talhoes?.length === 0) return {};
    const contagem: Record<string, number> = {};

    const operacoes = talhoes?.flatMap(t => t.planoOperacionalOperacaoTalhao);
    const classes = Array.from(new Set(operacoes.map(o => o.operacaoClasse)));

    classes.forEach(classe => {
      contagem[classe] = 0;
    });

    talhoes.forEach(talhao => {
      const operacoesTalhao: any[] = talhao.planoOperacionalOperacaoTalhao;

      classes.forEach(classe => {
        const operacoesPorClasse = operacoesTalhao.filter(o => o.operacaoClasse === classe);
        const quantidade = operacoesPorClasse.length;
        if (contagem[classe] < quantidade) {
          contagem[classe] = quantidade;
        }
      });
    });

    return contagem;
  }

  function getDataOrdenacaoMatriz(item: any) {
    const dataRef =
      item?.periodoDataInicio && item?.periodoDataFim
        ? item.periodoDataInicio
        : item?.dataProgramada;

    return dataRef ? new Date(dataRef).getTime() : Infinity;
  }

  function completeOperacoesTalhoes() {
    if (!result?.length) return result;

    const contagem = contagemClasses(result);
    const classes = Object.keys(contagem);

    const talhoesCompletos = result.map((talhao: any) => {
      const operacoes = [...talhao.planoOperacionalOperacaoTalhao];
      const contagemTalhao: Record<string, number> = {};

      operacoes.forEach(op => {
        contagemTalhao[op.operacaoClasse] = (contagemTalhao[op.operacaoClasse] || 0) + 1;
      });

      classes.forEach(classe => {
        const lack = (contagem[classe] || 0) - (contagemTalhao[classe] || 0);

        for (let i = 0; i < lack; i++) {
          operacoes.push({
            isPlaceholder: true,
            operacaoClasse: classe,
            disabled: true,
            error: false,
            errorFields: false,
          });
        }
      });

      return {
        ...talhao,
        planoOperacionalOperacaoTalhao: operacoes,
      };
    });

    const referencia = talhoesCompletos.reduce((melhor: any, atual: any) => {
      const reaisMelhor = melhor.planoOperacionalOperacaoTalhao.filter((o: any) => !o.isPlaceholder).length;
      const reaisAtual = atual.planoOperacionalOperacaoTalhao.filter((o: any) => !o.isPlaceholder).length;
      return reaisAtual > reaisMelhor ? atual : melhor;
    }, talhoesCompletos[0]);

    const ordemColunas = [...referencia.planoOperacionalOperacaoTalhao]
      .sort((a: any, b: any) => getDataOrdenacaoMatriz(a) - getDataOrdenacaoMatriz(b))
      .map((op: any) => op.operacaoClasse);

    return talhoesCompletos.map((talhao: any) => {
      const disponiveisPorClasse: Record<string, any[]> = {};

      talhao.planoOperacionalOperacaoTalhao.forEach((op: any) => {
        if (!disponiveisPorClasse[op.operacaoClasse]) {
          disponiveisPorClasse[op.operacaoClasse] = [];
        }
        disponiveisPorClasse[op.operacaoClasse].push(op);
      });

      Object.keys(disponiveisPorClasse).forEach(classe => {
        disponiveisPorClasse[classe].sort((a, b) => {
          if (a.isPlaceholder !== b.isPlaceholder) {
            return a.isPlaceholder ? 1 : -1;
          }
          return getDataOrdenacaoMatriz(a) - getDataOrdenacaoMatriz(b);
        });
      });

      const sorted = ordemColunas.map((classe: string) => {
        const lista = disponiveisPorClasse[classe];
        return lista?.shift() ?? {
          isPlaceholder: true,
          operacaoClasse: classe,
          disabled: true,
          error: false,
          errorFields: false,
        };
      });

      sorted.forEach((item: any, index: number) => {
        item.index = index + 1;
      });

      return {
        ...talhao,
        planoOperacionalOperacaoTalhao: sorted,
      };
    });
  }

  const orderedResult = completeOperacoesTalhoes();

  function aplicarErrosValidacaoMatriz(validateFields: ReturnType<typeof todasClassesSelecionadas>) {
    const filteredResult = result.map((item: any) => ({
      ...item,
      planoOperacionalOperacaoTalhao: item.planoOperacionalOperacaoTalhao.map((operacao: any) => {
        if (operacao.isPlaceholder || operacao.disabled) {
          return { ...operacao, error: false };
        }

        return {
          ...operacao,
          error: validateFields?.idsInvalidos.includes(operacao.planoOperacionalOperacaoTalhaoId),
        };
      }),
    }));

    setResult(filteredResult);
  }

  function validateOperationMatriz() {
    const set = new Set(planoOperacionalOperacaoTalhaoIds);
    const validateFields = todasClassesSelecionadas(orderedResult ?? [], set, true, {
      code: ordemServico?.code,
      id: ordemServico?.id,
      isEdit: isEdit,
    });
    if (!validateFields.valido) {
      aplicarErrosValidacaoMatriz(validateFields);
    }

    return validateFields;
  }

  function resetOperations() {
    const newResult = orderedResult;
    const filteredResult = newResult.map(
      (item: any, i: number) => {
        return ({
          ...item,
          planoOperacionalOperacaoTalhao: item.planoOperacionalOperacaoTalhao.map((operacao: any) => {

            return ({
              ...operacao,
              error: false,
              errorFields: false
            })
          })
        })
      }
    );
    setResult(filteredResult);
  }

  useEffect(() => {
    if (!isMatrizSubmitted) return;
    resetOperations();
    setIsMatrizSubmitted(true);
  }, [planoOperacionalOperacaoTalhaoIds.length])

  function handleOpenCadastroOrdemServico() {
    const validateFields = validateOperationMatriz();
    setIsMatrizSubmitted(true);
    if (!validateFields.valido) return;
    resetOperations();
    setIsMatrizOperacoesOpen(false);
    setIsCadastroOrdemServicoOpen(true);
  }

  function handleOpenLocaisRecursos() {
    setIsCadastroOrdemServicoOpen(false);
    setIsLocaisRecursosOpen(true);
  }

  function handleBackMatrizOperacoes() {
    setIsCadastroOrdemServicoOpen(false);
    setIsMatrizOperacoesOpen(true);
  }

  function handleBackCadastroOrdemServico() {
    setIsLocaisRecursosOpen(false);
    setIsCadastroOrdemServicoOpen(true);
  }

  function handleOpenResultado() {
    setIsLocaisRecursosOpen(false);
    setIsResultadoOpen(true);
  }

  function handleClose() {
    setClassesOperacoes([]);
    setPlanoOperacionalOperacaoTalhaoIds([]);
    setListTalhoesIds([]);
    setIsMatrizOperacoesOpen(false);
    setIsCadastroOrdemServicoOpen(false);
    setIsLocaisRecursosOpen(false);
    setIsResultadoOpen(false);
    onClose();
    setResult([]);
  }

  return (
    <>
      <ModalMatrizOperacoes
        data={data}
        isLoading={isLoading}
        talhoesIds={talhoesIds}
        isFetching={isFetching}
        planoOperacionalData={planoOperacionalData}
        isOpen={isMatrizOperacoesOpen}
        next={handleOpenCadastroOrdemServico}
        onClose={handleClose}
        setOperationName={setOperationName}
        filteredResult={result}
        orderedResult={isFetching ? [] : orderedResult}
        isEdit={isEdit}
        ordemServico={ordemServico}
        listData={listData}
      />

      <ModalCadastroOrdemServico
        safraPeriodoIdExterno={safraPeriodoIdExterno}
        unidadeIdExterno={unidadeIdExterno}
        planoOperacionalData={planoOperacionalData}
        isOpen={isCadastroOrdemServicoOpen}
        onClose={handleClose}
        back={handleBackMatrizOperacoes}
        next={handleOpenLocaisRecursos}
        isEdit={isEdit}
        ordemServico={ordemServico}
        ordemServicoData={ordemServiceData}
        orderedResult={isFetching ? [] : orderedResult}
        statusBlock={statuses}
      />

      <ModalLocaisRecursos
        data={data}
        listData={listData}
        loading={loadingCreate || loadingUpdate}
        isOpen={isLocaisRecursosOpen}
        onClose={handleClose}
        back={handleBackCadastroOrdemServico}
        handleCreate={handleCreateOrUpdate}
        isEdit={isEdit}
        orderedResult={isFetching ? [] : orderedResult}
        ordemServicoData={ordemServiceData}
        statusBlock={statuses}
        apontamentoTalhoes={apontamentoTalhoes}
        unidadeIdExterno={unidadeIdExterno}
        ordemServico={ordemServico}
      />

      <ModalResultado
        qtdTalhoes={qtdTalhoes}
        numeroOs={numeroOs}
        isOpen={isResultadoOpen}
        onClose={handleClose}
        isEdit={isEdit}
      />
    </>
  );
}

export default OrdemServico;
